import { useCallback, useState } from 'react';

type ResolveCallback = (result: boolean) => void;

export function useUserConfirmationFlow<T>(
  checkIfConfirmationIsRequired: (value: T) => Promise<boolean>,
): [
  showConfirmModal: boolean,
  startConfirmationFlow: (value: T) => Promise<boolean>,
  confirm: () => void,
  cancel: () => void,
] {
  const [resolveCallback, setResolveCallback] = useState<ResolveCallback | undefined>();
  const [userConfirmed, setUserConfirmed] = useState(false);

  const showConfirmModal = !userConfirmed && !!resolveCallback;

  const startConfirmationFlow = useCallback(
    async (value: T) => {
      const userConfirmed = await new Promise<boolean>((resolve) => {
        setUserConfirmed((currentValue) => {
          resolve(currentValue);
          return currentValue;
        });
      });

      if (userConfirmed) {
        return true;
      }

      try {
        const confirmationRequired = await checkIfConfirmationIsRequired(value);

        if (!confirmationRequired) {
          return true;
        }
      } catch (error) {
        console.error(`Exception during confirmation flow:`, error);
      }

      return new Promise<boolean>((resolve) => setResolveCallback(() => resolve));
    },
    [checkIfConfirmationIsRequired],
  );

  const confirm = useCallback(() => {
    setUserConfirmed(true);
    resolveCallback?.(true);
    setResolveCallback(undefined);
  }, [resolveCallback]);

  const cancel = useCallback(() => {
    resolveCallback?.(false);
    setResolveCallback(undefined);
  }, [resolveCallback]);

  return [showConfirmModal, startConfirmationFlow, confirm, cancel] as const;
}
